import { useEffect, useState } from "react";
import { adminApi } from "../../api/adminApi";
import type { ApiKey } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";

const SCOPE_OPTIONS = ["REPORT_EXECUTE", "REPORT_READ", "ADMIN"];

export default function ApiKeyManager() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [scopes, setScopes] = useState<string[]>(["REPORT_EXECUTE"]);
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<ApiKey | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ApiKey | null>(null);

  function load() {
    setLoading(true);
    adminApi.apiKeys
      .list()
      .then(setKeys)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load API keys"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function toggleScope(scope: string) {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  }

  async function handleCreate() {
    if (!label.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const created = await adminApi.apiKeys.create(label.trim(), scopes);
      setRevealedKey(created);
      setLabel("");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create API key");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke() {
    if (!pendingRevoke) return;
    try {
      await adminApi.apiKeys.revoke(pendingRevoke.id);
      setPendingRevoke(null);
      load();
    } catch (err) {
      setPendingRevoke(null);
      setError(err instanceof Error ? err.message : "Failed to revoke API key");
    }
  }

  function copySecret() {
    if (revealedKey?.fullKey) {
      navigator.clipboard?.writeText(revealedKey.fullKey).catch(() => undefined);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>API Keys</h1>
      </div>
      {error && <div className="error-text">{error}</div>}

      <div className="page-panel">
        <h3>New API key</h3>
        <div className="form-row">
          <label>Label</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ExtJS UAP integration" />
        </div>
        <div className="form-row">
          <label>Scopes</label>
          <div style={{ display: "flex", gap: 12 }}>
            {SCOPE_OPTIONS.map((s) => (
              <label key={s} style={{ fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={() => toggleScope(s)}
                  style={{ marginRight: 4 }}
                />
                {s}
              </label>
            ))}
          </div>
        </div>
        <button type="button" className="btn-primary" onClick={handleCreate} disabled={creating}>
          {creating ? "Creating..." : "Create key"}
        </button>
      </div>

      {revealedKey?.fullKey && (
        <div className="page-panel" style={{ borderColor: "#f59e0b" }}>
          <h4>Copy your new API key now</h4>
          <p className="help-text">
            This is the only time the full secret is shown. Store it securely — it can't be retrieved
            again.
          </p>
          <div className="code-block">{revealedKey.fullKey}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="btn-secondary" onClick={copySecret}>
              Copy
            </button>
            <button type="button" className="btn-secondary" onClick={() => setRevealedKey(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="page-panel">
        {loading ? (
          <p>Loading...</p>
        ) : (
          <DataTable
            rows={keys}
            rowKey={(k) => k.id}
            emptyMessage="No API keys yet."
            columns={[
              { key: "label", header: "Label" },
              { key: "keyPrefix", header: "Prefix", render: (k) => `${k.keyPrefix}...` },
              { key: "scopes", header: "Scopes", render: (k) => k.scopes.join(", ") },
              {
                key: "status",
                header: "Status",
                render: (k) => (
                  <span className={k.status === "ACTIVE" ? "badge badge-published" : "badge badge-archived"}>
                    {k.status}
                  </span>
                ),
              },
              { key: "lastUsedAt", header: "Last used", render: (k) => k.lastUsedAt ?? "never" },
              {
                key: "actions",
                header: "",
                render: (k) =>
                  k.status === "ACTIVE" ? (
                    <button type="button" className="btn-danger" onClick={() => setPendingRevoke(k)}>
                      Revoke
                    </button>
                  ) : null,
              },
            ]}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!pendingRevoke}
        title="Revoke API key"
        message={`Revoke "${pendingRevoke?.label}"? Any integration using it will stop working immediately.`}
        confirmLabel="Revoke"
        danger
        onConfirm={handleRevoke}
        onCancel={() => setPendingRevoke(null)}
      />
    </div>
  );
}
